/**
 * Interface representing the main ticket booking model.
 */
export interface TicketBookingModel {
  register_types: registerTypeModel[]; // List of registration types available for booking.
  user_discounts: loyaltyApplicableModel[]; // List of applicable user discounts.
}

/**
 * Interface representing the loyalty discount details.
 */
export interface loyaltyApplicableModel {
  code: string; // Discount code.
  discount_amount: string; // Discount amount as a string to handle decimals.
  display_name: string; // Display name of the discount.
  id: string; // Unique identifier for the discount.
}

/**
 * Interface representing the details of a registration type.
 */
export interface registerTypeModel {
  app_display_name: string; // Display name in the app.
  badge_type: string; // Type of badge associated with the ticket.
  base_price: string; // Base price of the ticket as a string.
  charges_for_subscriber: string; // Charges for subscribers as a string.
  code: string; // Unique code for the registration type.
  currency: string; // Currency of the ticket price.
  display_price: number; // Display price of the ticket as a number.
  email_name: string; // Email name associated with the ticket.
  entry_type: string; // Type of entry (e.g., single, multi).
  event_id: number; // ID of the associated event.
  for: string; // Purpose or audience of the ticket.
  gp_holders: any; // Can be null or an array of GP holders.
  id: number; // Unique identifier for the registration type.
  is_invite: boolean; // Indicates if the ticket is invite-only.
  is_online: boolean; // Indicates if the ticket is available online.
  is_pre_register: boolean; // Indicates if pre-registration is required.
  max_ticket_for_order: number; // Maximum tickets allowed per order.
  max_ticket_for_user: number; // Maximum tickets allowed per user.
  name: string; // Name of the ticket.
  paid_amount: number; // Amount paid for the ticket.
  register_instructions: string; // Instructions for registration.
  sms_name: string; // SMS name associated with the ticket.
  ticket_note: string | null; // Additional notes for the ticket (nullable).
  count: number; // Number of tickets selected.
  isSelected: boolean; // Indicates if the ticket is selected.
}

/**
 * Constant representing the images for tickets before selection.
 */
export const ticketsImagesBeforeSelecteingc = {
  multi_entry: {
    black: "assets/images/all_day_black.png",
    color: "assets/images/all_day_color.png",
    white: "assets/images/all_day_pass_white.png",
  },
  biz_pass: {
    black: "assets/images/business_bass_black.png",
    color: "assets/images/business_bass_color.png",
    white: "assets/images/business_white.png",
  },
  child_single_entry: {
    black: "assets/images/child_black.png",
    color: "assets/images/child_color.png",
    white: "assets/images/child_white.png",
  },
  first_day_single_entry: {
    black: "assets/images/first_day_black.png",
    color: "assets/images/first_day_color.png",
    white: "assets/images/first_day_white.png",
  },
  single_entry: {
    black: "assets/images/single_entry_black.png",
    color: "assets/images/single_entry_color.png",
    white: "assets/images/single_entry_white.png",
  },
  standard_invite_by_exh: {
    black: "assets/images/single_entry_black.png",
    color: "assets/images/single_entry_color.png",
    white: "assets/images/single_entry_white.png",
  },
};

/**
 * Constant representing the color themes for different ticket types.
 */
export const ticketColorsModal = {
  multi_entry: {
    primary: "#894700",
    darkPrimary: "#5E3F00",
    secondary: "#FFF6E6",

    border: "#FFE49E",
    qrCode: "#815201",
    ticketDetailsColorGradientBackground:
      "conic-gradient(from 250.69deg at 34.69% 87.86%, #895700 -79.62deg, #955F01 77.66deg, #E08F00 123.86deg, #FFB330 149.22deg, #E9990D 156.16deg, #FFB330 157.93deg, #E08F00 163.54deg, #955F01 177.13deg, #955F01 193.82deg, #955F01 280.38deg, #895700 437.66deg)",
    colorGradientBackground:
      "conic-gradient(from -29.06deg at 79.28% 89.47%, #664101 -19.98deg, #B67301 6.92deg, #A76800 6.96deg, #935D00 221.54deg, #664101 340.02deg, #B67301 366.92deg)",
    ticketNote: {
      line1: "Multiple entries for 1 person",
      line2: "Valid for all event days",
    },
  },
  biz_pass: {
    primary: "#0063CE",
    darkPrimary: "#023573",
    secondary: "#E6F2FF",

    border: "#DDFFFC",
    qrCode: "#02397F",
    ticketDetailsColorGradientBackground:
      "conic-gradient(from 250.69deg at 34.69% 87.86%, #012D64 -79.62deg, #033675 77.66deg, #026CF1 123.86deg, #228CFE 149.22deg, #6ED5FF 156.16deg, #59BDFF 157.93deg, #0F79F6 163.54deg, #033675 177.13deg, #033675 193.82deg, #033675 280.38deg, #033675 437.66deg)",

    colorGradientBackground:
      "conic-gradient(from -29.06deg at 79.28% 89.47%, #054AA1 -12.12deg, #0080F7 6.92deg, #04499E 6.96deg, #012D65 221.54deg, #054AA1 347.88deg, #0080F7 366.92deg)",
    ticketNote: {
      line1: "All days pass for 1 person",
      line2: "Access to networking lounge",
    },
  },
  child_single_entry: {
    primary: "#D9A900",
    darkPrimary: "#CC9F00",
    secondary: "#FFFADD",

    border: "#E6FFA7",
    qrCode: "#D9A900",
    ticketDetailsColorGradientBackground:
      "conic-gradient(from 250.69deg at 34.69% 87.86%, #FFC804 -79.62deg, #FFC702 77.66deg, #EEB901 123.86deg, #FFDF70  149.22deg, #FFDF70 156.16deg, #FFDF70 157.93deg, #FFEAA3 163.54deg, #FFC806 177.13deg, #FFC806 193.82deg, #FFC806 280.38deg, #FFC806 437.66deg)",

    colorGradientBackground:
      "conic-gradient(from -29.06deg at 79.28% 89.47%, #E2B30D 0deg, #FFC500 1.11deg, #FFD43B 5.71deg, #FFD12E 5.74deg, #FFD334 223.27deg, #E2B30D 360deg)",
    ticketNote: {
      line1: "Single entry for 5-12 yr kids",
      line2: "Must be with parent",
    },
  },
  first_day_single_entry: {
    primary: "#EF8A04",
    darkPrimary: "#D57500",
    secondary: "#FFF7E4",

    border: "#FFEE9D",
    qrCode: "#D57500",
    ticketDetailsColorGradientBackground:
      "conic-gradient(from 250.69deg at 34.69% 87.86%, #D57500 -79.62deg, #D57500 77.66deg, #FFA224 123.86deg, #FFCE7A 149.22deg, #FFF0A0 156.16deg, #FFF6C8 157.93deg, #FFA224 163.54deg, #D57500 177.13deg, #D87C0A 193.82deg, #D57500 280.38deg, #D57500 437.66deg)",

    colorGradientBackground:
      "conic-gradient(from -29.06deg at 79.28% 89.47%, #FF9915 -12.12deg, #FFDD82 6.92deg, #FFA629 6.96deg, #D57500 221.54deg, #FF9915 347.88deg, #FFDD82 366.92deg)",
    ticketNote: {
      line1: "Single entry pass",
      line2: "Valid only on 10th Dec",
    },
  },
  single_entry: {
    primary: "#008940",
    darkPrimary: "#00411E",
    secondary: "#EFFFF7",
    border: "#98FFA5",
    qrCode: "#005D2B",
    ticketDetailsColorGradientBackground:
      "conic-gradient(from 250.69deg at 34.69% 87.86%, #014C24 -79.62deg, #004C24 77.66deg, #017E3B 123.86deg, #00CF61 149.22deg, #019948 156.16deg, #00BD58 157.93deg, #017E3B 163.54deg, #017E3B 177.13deg, #014C24 193.82deg, #004C24 280.38deg, #004C24 437.66deg)",

    colorGradientBackground:
      "conic-gradient(from -29.06deg at 79.28% 89.47%, #036C34 -12.12deg, #008940 6.92deg, #017D3B 6.96deg, #05592C 221.54deg, #036C34 347.88deg, #008940 366.92deg)",
    ticketNote: {
      line1: "One-time entry for 1 person",
      line2: "Attend on any event day",
    },
  },
  standard_invite_by_exh: {
    primary: "#008940",
    darkPrimary: "#00411E",
    secondary: "#EFFFF7",
    border: "#98FFA5",
    qrCode: "#005D2B",
    ticketDetailsColorGradientBackground:
      "conic-gradient(from 250.69deg at 34.69% 87.86%, #014C24 -79.62deg, #004C24 77.66deg, #017E3B 123.86deg, #00CF61 149.22deg, #019948 156.16deg, #00BD58 157.93deg, #017E3B 163.54deg, #017E3B 177.13deg, #014C24 193.82deg, #004C24 280.38deg, #004C24 437.66deg)",

    colorGradientBackground:
      "conic-gradient(from -29.06deg at 79.28% 89.47%, #036C34 -12.12deg, #008940 6.92deg, #017D3B 6.96deg, #05592C 221.54deg, #036C34 347.88deg, #008940 366.92deg)",
    ticketNote: {
      line1: "One-time entry for 1 person",
      line2: "Attend on any event day",
    },
  },
};

/**
 * Interface representing the selected registration types.
 */
export interface selectRegTypes {
  ticket_code: string; // Code of the selected ticket.
  quantity: number; // Quantity of tickets selected.
}

/**
 * Interface representing the request body for registering ticket types.
 */
export interface registerTypeReqBodyModel {
  language: string; // Language preference.
  source: string; // Source of the request.
  select_reg_types: selectRegTypes[]; // List of selected registration types.
  ref_source?: string; // Optional reference source.
}

/**
 * Interface representing the response model for registering ticket types.
 */
export interface registerTypeResModel {
  summary_id: string; // Summary ID of the registration.
}

/**
 * Interface representing the response model for initiating a payment.
 */
export interface initiatePaymentResModel {
  amount: number; // Total amount for the payment.
  amount_due: number; // Amount still due.
  amount_paid: number; // Amount already paid.
  attempts: number; // Number of payment attempts.
  created_at: string; // Timestamp of payment creation.
  currency: string; // Currency of the payment.
  entity: string; // Entity type of the payment.
  id: string; // Unique identifier for the payment.
  notes: {
    payment_for: string; // Purpose of the payment.
  };
  offer_id: string; // Offer ID associated with the payment.
  receipt: string; // Receipt number.
  status: string; // Status of the payment.
  payment_for: string; // Purpose of the payment.
  payment_for_item_id: string; // ID of the item being paid for.
  payment_for_item_uuid: string; // UUID of the item being paid for.
  razorpay_order_id: string; // Razorpay order ID.
  summary_id: string; // Summary ID of the payment.
  razorpay_key: string; // Razorpay key used for the payment.
  selected_language: string; // Selected language for the payment.
  razorpay_authorised_datetime: string; // Timestamp of Razorpay authorization (nullable).
}

/**
 * Interface representing the response model for registration details.
 */
export interface registrationDetailsResModel {
  details: Details; // General details of the registration.
  people_data: {}; // Data about the people registered.
  ticket_type_details: ticketTypeDeails[]; // Details of the ticket type.
}

/**
 * Interface representing the details of a ticket type.
 */
export interface ticketTypeDeails {
  adult_count: number; // Number of adult tickets.
  child_count: number; // Number of child tickets.
  code: string; // Code of the ticket type.
  display_price: number; // Display price of the ticket.
  event_register_summary_id: string; // Summary ID of the event registration.
  event_register_type_id: string; // Type ID of the event registration.
  id: string; // Unique identifier for the ticket type.
  name: string; // Name of the ticket type.
  paid_amount: string; // Amount paid for the ticket.
  register_people_count: string; // Number of people registered.
}
// details.model.ts
export interface Details {
  id: string;
  language: string;
  created_datetime: string; // ISO string
  created_by_userid: number;
  transaction_id: string | null;
  event_id: number;
  status: string;
  is_upgrade: boolean;
  total_people: number;
  total_amount: number;
  source: string;
  first_name: string;
  last_name: string;
  mobile1: string;
  email: string | null;
  country_code: string;
  razorpay_payment_id: string | null;
  payment_status: string | null;
  razorpay_signature: string | null;
  paid_amount: number;
  discount_amount: number;
  transaction_created_datetime: string | null;
  razorpay_authorised_datetime: string | null;
  is_farmer_first_flow_used: boolean;
  adult_count: number;
  child_count: number;
  whatsapp_opt_in_status: boolean;
}

/**
 * This interface represents the request payload
 * sent to the backend to verify a Razorpay payment.
 */
export interface VerifyRazorpayPaymentRequest {
  /**
   * The unique identifier of the Razorpay order
   * generated during payment initiation.
   */
  razorpay_order_id: string;

  /**
   * The unique identifier of the payment
   * generated after the user completes the payment.
   */
  razorpay_payment_id: string;

  /**
   * The cryptographic signature generated by Razorpay
   * for validating the authenticity of the payment.
   */
  razorpay_signature: string;
}

// Model representing the failure payment result
export interface failurePaymentResultModel {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  error_code: string;
  error_description: string;
}

// Model representing the failure payment result
export interface failurePaymentUpgradeResultModel {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  error_code: string;
  error_description: string;
  summary_id: string;
}

/* Razorpay Payment Verification Response Model after verifying the payment with Razorpay*/
export interface VerifyRazorpayPaymentResponse {
  /**verified successfully or failed.*/
  message: string;
}

export const DIVIDEND_FACTOR: number = 100;